import { BASE_URL } from "@/assets/constants/constants";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { Icon } from "@/components/icons/Icon";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useQuestionsForm } from "@/store/useQuestionsForm";
import type { QuestionFormType } from "@/types/types";
import { Link, useNavigate, useParams } from "react-router-dom";
import ModalAddQuestion from "./modal-add-question/ModalAddQuestion";
import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateQuestions } from "@/lib/services/questions";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/context/loader-context/LoaderContext";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";

const ChecklistIdHeader = ({ checklist }: { checklist: QuestionFormType }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { isEditing, updateIsEditing, questions, edited } = useQuestionsForm();
  const queryClient = useQueryClient();
  const { checklistId } = useParams() as { checklistId: string };

  const { mutateAsync } = useMutation({
    mutationFn: updateQuestions,
    onSuccess: () => {
      appToast.success(
        translate("inspectionForm"),
        translate("newInspectionType", [checklist.type]),
      );
      hideLoader();
      navigate(BASE_URL + "/checklist");
    },
    onError: (err) => {
      appToast.error(translate("error"), err.message);
      hideLoader();
    },
  });

  const handleOnSave = async () => {
    showLoader();
    await mutateAsync({ type: checklist.type, questions });
  };

  const handleonCancel = () => {
    queryClient.invalidateQueries({ queryKey: ["checklistId", checklistId] });
    updateIsEditing(false);
  };

  useEffect(() => {
    return () => updateIsEditing(false);
  }, []);

  return (
    <>
      <div className="flex justify-between">
        <div>
          <Link to={`${BASE_URL}/checklist`}>
            <div className="flex space-x-2 hover:underline text-ib-red-main hover:text-ib-red-dark">
              <Icon name={"arrowLeft"} />
              <span>{translate("goBack")} </span>
            </div>
          </Link>
        </div>
        <div>
          {isEditing ? (
            <div className="flex space-x-6">
              <SecondaryButton
                onClick={handleonCancel}
                icon="close"
                label={translate("cancel")}
                colour={"red"}
                size="small"
              />
              <ModalAddQuestion />
              <PrimaryButton
                disabled={!edited}
                onClick={handleOnSave}
                icon="check"
                label={translate("save")}
                colour={"red"}
                size="small"
              />
            </div>
          ) : (
            <PrimaryButton
              onClick={() => updateIsEditing(true)}
              icon="pencil"
              label={translate("edit")}
              colour={"red"}
              size="small"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ChecklistIdHeader;
